import React from 'react';
import { mcpSetupExamples } from '../utils/mcpSetup';

type Props = {
  endpoint: string;
  trustedApiOrigin: string;
  cliCommand: string;
  copied: string | null;
  copy: (label: string, value: string) => Promise<void>;
};

export default function McpSetup({ endpoint, trustedApiOrigin, cliCommand, copied, copy }: Props) {
  let examples: ReturnType<typeof mcpSetupExamples>;
  try {
    examples = mcpSetupExamples(endpoint, trustedApiOrigin);
  } catch {
    return (
      <section className="mcp-setup" aria-labelledby="mcp-setup-title">
        <h3 id="mcp-setup-title">Connect a client</h3>
        <p className="mcp-setup__notice" role="status">MCP setup unavailable: the endpoint is invalid or does not match this API origin. Ask your administrator to correct the public API URL. Token display and access controls remain available below.</p>
        <p>ChatGPT OAuth is unavailable in this token-only version.</p>
      </section>
    );
  }
  const snippet = (label: string, value: string) => (
    <div className="mcp-setup__snippet">
      <div className="token-snippet-header">
        <span>{label}</span>
        <button type="button" className="ghost" onClick={() => void copy(label, value)}>
          {copied === label ? 'Copied' : `Copy ${label}`}
        </button>
      </div>
      <pre tabIndex={0} aria-label={label}><code>{value}</code></pre>
    </div>
  );
  return (
    <section className="mcp-setup" aria-labelledby="mcp-setup-title">
      <div className="mcp-setup__heading">
        <div>
          <h3 id="mcp-setup-title">Connect a client</h3>
          <p className="status">Choose your setup. Each client keeps the permissions of its token.</p>
        </div>
        <span className="mcp-setup__tag">MCP · Streamable HTTP</span>
      </div>
      <div className="mcp-setup__endpoint">
        <div><span className="status">MCP endpoint</span><code>{examples.url}</code></div>
        <button type="button" className="ghost" onClick={() => void copy('endpoint', examples.url)}>
          {copied === 'endpoint' ? 'Copied' : 'Copy endpoint'}
        </button>
      </div>
      <div className="mcp-setup__clients">
        <details>
          <summary><span>Mission Control CLI<small>Interactive login · local automation</small></span></summary>
          <div className="mcp-setup__body">
            <p>Run this command, then paste your API token when prompted. Access options reflect the token form.</p>
            {snippet('CLI command', cliCommand)}
            <p className="status">This command requires CLI administrator support. See the <a href="/docs/mission-control-cli-quickstart.html">CLI installation guide</a>.</p>
            <p className="status">Keep credentials out of shell history, chats and source control.</p>
          </div>
        </details>
        <details>
          <summary><span>Codex<small>config.toml · API token</small></span></summary>
          <div className="mcp-setup__body">
            <p>Set <code>PROJ_CREATOR_TOOL_TOKEN</code> in the environment that launches Codex. Add this to your Codex <code>config.toml</code>.</p>
            {snippet('Codex configuration', examples.codex)}
          </div>
        </details>
        <details>
          <summary><span>Claude Code<small>.mcp.json · API token</small></span></summary>
          <div className="mcp-setup__body">
            <p>Set <code>PROJ_CREATOR_TOOL_TOKEN</code> before starting Claude Code. Merge this entry into your project’s <code>.mcp.json</code>.</p>
            {snippet('Claude configuration', examples.claude)}
          </div>
        </details>
        <details>
          <summary><span>ChatGPT<small>OAuth unsupported · setup requirements</small></span></summary>
          <div className="mcp-setup__body">
            <p>ChatGPT uses an OAuth connection, not an API token pasted into chat. An authenticated connection needs a public HTTPS MCP endpoint and identity-provider discovery.</p>
            <p className="mcp-setup__notice">ChatGPT OAuth is unavailable: this version supports API-token MCP access only. The endpoint above is not a ChatGPT OAuth connection. OAuth resource-server support and identity-provider configuration are still required; creating a token here does not enable them.</p>
            <p>Developer mode and custom connections depend on your account and workspace policy. Never use anonymous access to bypass sign-in.</p>
            <a href="https://developers.openai.com/apps-sdk/deploy/connect-chatgpt/" target="_blank" rel="noreferrer">OpenAI connection guide ↗</a>
          </div>
        </details>
        <details className="mcp-setup__diagnostics">
          <summary><span>Test the connection<small>curl · protocol handshake</small></span></summary>
          <div className="mcp-setup__body">
            <p>Check the public MCP handshake without sending credentials. This does not invoke a tool or grant additional access.</p>
            {snippet('handshake command', examples.http)}
            <p className="status">Initialize is public on this API-token endpoint. Success checks the protocol only, not your credentials. Tool requests still require a valid token and its apps and scopes.</p>
          </div>
        </details>
      </div>
    </section>
  );
}
